import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Search } from "react-feather";
import { toast } from "react-toastify";
import {
  getTeacherLedger,
  getTeachers,
  rejectTeacherLedgerDispute,
  teacherProfile,
  type TeacherLedgerResponse,
} from "../../../../Api/apiUtils";
import {
  buildLedgerTabs,
  buildPayoutBreakdownFromSummary,
  filterLedgerLines,
  formatCurrency,
  formatLedgerTabLabel,
  formatSummaryTotalInr,
  getMonthRange,
  groupLedgerLines,
  isAllLedgerTab,
  type LedgerTransaction,
} from "./teacherLedgerUtils";
import TeacherLedgerAdjustmentModal, {
  type AdjustmentModalTarget,
} from "./teacherLedgerAdjustmentModal";
import { useHasEmailAccess } from "../../../../utils/emailAccessConfig";
import { MixpanelEvents, trackEvent } from "../../../../utils/mixpanelTracking";
import {
  Badge,
  Button,
  Callout,
  Card,
  Dialog,
  EmptyState,
  Field,
  Loader,
  Page,
  PageHeader,
  SearchInput,
  SegmentedControl,
  Select,
  Table,
  TextArea,
  Toolbar,
} from "../../../../ui";
import type { BadgeTone } from "../../../../ui";
import "./teacherLedger.scss";

type TeacherListItem = {
  teacherId: string;
  teacherName: string;
  emailId?: string;
  mobileNumber?: string;
  credentialId?: string;
};

type SelectedTeacher = TeacherListItem & {
  teacherUserId: string;
  courseName?: string;
};

const MONTH_OPTIONS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Badge class names from teacherLedgerUtils.buildLineBadges → design-system tones.
const BADGE_TONES: Record<string, BadgeTone> = {
  "tl-badge-amber": "warning",
  "tl-badge-teal": "info",
  "tl-badge-stone": "neutral",
  "tl-badge-rose-soft": "danger",
  "tl-badge-emerald-soft": "success",
  "tl-badge-orange": "warning",
  "tl-badge-violet": "primary",
};

const getLedgerPeriodProps = (month: number, year: number) => {
  const { fromDate, toDate } = getMonthRange(year, month);
  return {
    month: MONTH_OPTIONS[month],
    year: String(year),
    fromDate,
    toDate,
  };
};

const getTeacherLedgerAnalyticsProps = (
  selectedTeacher: SelectedTeacher | null,
  month: number,
  year: number,
  extra: Record<string, unknown> = {}
) => ({
  ...getLedgerPeriodProps(month, year),
  teacherId: selectedTeacher?.teacherId ?? null,
  teacherUserId: selectedTeacher?.teacherUserId ?? null,
  teacherName: selectedTeacher?.teacherName ?? null,
  ...extra,
});

const TeacherLedger: React.FC = () => {
  const { ready: accessReady, allowed: isAllowed } = useHasEmailAccess("teacherLedger");

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const preselectedTeacherId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("teacherId") || "";
  }, []);

  const [searchQuery, setSearchQuery] = useState("");
  const [teacherModalOpen, setTeacherModalOpen] = useState(false);
  const [teacherResults, setTeacherResults] = useState<TeacherListItem[]>([]);
  const [teacherSearchLoading, setTeacherSearchLoading] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState<SelectedTeacher | null>(null);
  const [teacherProfileLoading, setTeacherProfileLoading] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [activeFilter, setActiveFilter] = useState("ALL");
  const [ledgerData, setLedgerData] = useState<TeacherLedgerResponse | null>(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [adjustmentModalOpen, setAdjustmentModalOpen] = useState(false);
  const [adjustmentTarget, setAdjustmentTarget] = useState<AdjustmentModalTarget | null>(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<LedgerTransaction | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [rejectSavingId, setRejectSavingId] = useState<string | number | null>(null);
  const openAdjustmentModal = (target: AdjustmentModalTarget) => {
    setAdjustmentTarget(target);
    setAdjustmentModalOpen(true);
  };

  const closeAdjustmentModal = () => {
    setAdjustmentModalOpen(false);
    setAdjustmentTarget(null);
  };

  const openRejectModal = (transaction: LedgerTransaction) => {
    setRejectTarget(transaction);
    setRejectReason("");
    setRejectModalOpen(true);
  };

  const closeRejectModal = () => {
    if (rejectSubmitting) return;
    setRejectModalOpen(false);
    setRejectTarget(null);
    setRejectReason("");
  };

  const handleRejectDispute = async () => {
    if (!rejectTarget || !selectedTeacher?.teacherId) return;

    const reason = rejectReason.trim();
    if (!reason) {
      toast.error("Please enter a reason for rejecting this dispute.");
      return;
    }

    const ledgerLineId = Number(rejectTarget.id);
    const teacherId = Number(rejectTarget.teacherId ?? selectedTeacher.teacherId);
    if (!Number.isFinite(ledgerLineId) || !Number.isFinite(teacherId)) {
      toast.error("Missing ledger or teacher details.");
      return;
    }

    setRejectSubmitting(true);
    setRejectSavingId(rejectTarget.id);
    try {
      await rejectTeacherLedgerDispute({
        ledgerLineId,
        teacherId,
        reason,
      });
      trackEvent(MixpanelEvents.TEACHER_LEDGER_DISPUTE_REJECTED_SUCCESS, {
        ...getTeacherLedgerAnalyticsProps(selectedTeacher, selectedMonth, selectedYear),
        ledgerLineId,
        teacherId,
      });
      toast.success("Dispute rejected.");
      closeRejectModal();
      await fetchTeacherLedger();
    } catch (error: any) {
      trackEvent(MixpanelEvents.TEACHER_LEDGER_DISPUTE_REJECTED_FAILURE, {
        ...getTeacherLedgerAnalyticsProps(selectedTeacher, selectedMonth, selectedYear),
        ledgerLineId,
        teacherId,
        errorMessage: error?.response?.data?.message || error?.message || "Failed to reject dispute",
      });
      toast.error(error?.response?.data?.message || "Failed to reject dispute.");
    } finally {
      setRejectSubmitting(false);
      setRejectSavingId(null);
    }
  };

  const isFutureMonth = (month: number, year: number) =>
    year > currentYear || (year === currentYear && month > currentMonth);

  const { fromDate, toDate } = useMemo(
    () => getMonthRange(selectedYear, selectedMonth),
    [selectedMonth, selectedYear]
  );

  const fetchTeachers = async (query: string) => {
    setTeacherSearchLoading(true);
    try {
      let payload: { name?: string; emailId?: string; mobileNumber?: string } = {};
      if (query.trim()) {
        if (/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,15}/i.test(query)) {
          payload = { emailId: query.trim() };
        } else if (/^[0-9\b]+$/.test(query.trim())) {
          payload = { mobileNumber: query.trim() };
        } else {
          payload = { name: query.trim() };
        }
      }
      const teachers = await getTeachers(0, 1000, payload);
      setTeacherResults(Array.isArray(teachers) ? teachers : []);
    } catch (error) {
      console.error("Error searching teachers:", error);
      toast.error("Failed to search teachers.");
      setTeacherResults([]);
    } finally {
      setTeacherSearchLoading(false);
    }
  };

  const handleOpenTeacherSearch = () => {
    setTeacherModalOpen(true);
    void fetchTeachers(searchQuery);
  };

  useEffect(() => {
    if (!teacherModalOpen) return;

    const debounceTimer = window.setTimeout(() => {
      void fetchTeachers(searchQuery);
    }, 400);

    return () => {
      window.clearTimeout(debounceTimer);
    };
  }, [searchQuery, teacherModalOpen]);

  const handleSelectTeacher = async (teacher: TeacherListItem) => {
    setTeacherModalOpen(false);
    setTeacherProfileLoading(true);
    try {
      const profile = await teacherProfile(teacher.teacherId);
      const teacherUserId = profile?.teacherProfileDetails?.userId;
      if (!teacherUserId) {
        toast.error("Could not resolve teacher user id.");
        return;
      }
      setSelectedTeacher({
        ...teacher,
        teacherUserId: String(teacherUserId),
        courseName: profile?.teacherProfileDetails?.courseName,
      });
    } catch (error) {
      console.error("Error loading teacher profile:", error);
      toast.error("Failed to load teacher profile.");
    } finally {
      setTeacherProfileLoading(false);
    }
  };

  useEffect(() => {
    if (!preselectedTeacherId) return;

    let cancelled = false;
    const bootstrapTeacherFromQuery = async () => {
      setTeacherProfileLoading(true);
      try {
        const profile = await teacherProfile(preselectedTeacherId);
        if (cancelled) return;

        const teacherProfileDetails = profile?.teacherProfileDetails;
        const teacherUserId = teacherProfileDetails?.userId;
        if (!teacherUserId) {
          toast.error("Could not resolve teacher from URL.");
          return;
        }

        setSelectedTeacher({
          teacherId: String(preselectedTeacherId),
          teacherName: `${teacherProfileDetails?.firstName || ""} ${teacherProfileDetails?.lastName || ""}`.trim() || "Teacher",
          emailId: teacherProfileDetails?.emailId,
          mobileNumber: teacherProfileDetails?.mobileNumber,
          credentialId: teacherProfileDetails?.credentialId,
          teacherUserId: String(teacherUserId),
          courseName: teacherProfileDetails?.courseName,
        });
      } catch (error) {
        console.error("Error resolving preselected teacher:", error);
        toast.error("Failed to load teacher from URL.");
      } finally {
        if (!cancelled) setTeacherProfileLoading(false);
      }
    };

    void bootstrapTeacherFromQuery();
    return () => {
      cancelled = true;
    };
  }, [preselectedTeacherId]);

  const fetchTeacherLedger = useCallback(async () => {
    if (!selectedTeacher?.teacherUserId) return;

    try {
      setLedgerLoading(true);
      const data = await getTeacherLedger(selectedTeacher.teacherUserId, fromDate, toDate);
      setLedgerData(data);
      trackEvent(MixpanelEvents.TEACHER_LEDGER_DATA_LOADED, {
        ...getTeacherLedgerAnalyticsProps(selectedTeacher, selectedMonth, selectedYear),
        lineCount: data?.lines?.length ?? 0,
        totalInr: data?.summary?.TOTAL_INR ?? null,
      });
    } catch (error: any) {
      console.error("Error fetching teacher ledger:", error);
      trackEvent(MixpanelEvents.TEACHER_LEDGER_DATA_LOAD_FAILURE, {
        ...getTeacherLedgerAnalyticsProps(selectedTeacher, selectedMonth, selectedYear),
        errorMessage: error?.response?.data?.message || error?.message || "Failed to fetch ledger data",
      });
      toast.error(error?.response?.data?.message || "Failed to fetch ledger data.");
      setLedgerData(null);
    } finally {
      setLedgerLoading(false);
    }
  }, [selectedTeacher, selectedMonth, selectedYear, fromDate, toDate]);

  useEffect(() => {
    if (selectedTeacher?.teacherUserId) {
      void fetchTeacherLedger();
    } else {
      setLedgerData(null);
    }
  }, [fetchTeacherLedger, selectedTeacher?.teacherUserId]);

  const ledgerLines = ledgerData?.lines ?? [];

  const ledgerTabs = useMemo(
    () => buildLedgerTabs(ledgerData?.ledgerTabs, ledgerLines),
    [ledgerData?.ledgerTabs, ledgerLines]
  );

  useEffect(() => {
    const stillVisible = ledgerTabs.some(
      (tab) => tab.toUpperCase() === activeFilter.toUpperCase()
    );
    if (!stillVisible) {
      setActiveFilter(ledgerTabs.find((tab) => isAllLedgerTab(tab)) ?? "ALL");
    }
  }, [activeFilter, ledgerTabs]);

  const filteredGroups = useMemo(() => {
    const filteredLines = filterLedgerLines(ledgerLines, activeFilter);
    return groupLedgerLines(filteredLines);
  }, [ledgerLines, activeFilter]);

  const payoutBreakdown = useMemo(
    () => buildPayoutBreakdownFromSummary(ledgerData?.summary),
    [ledgerData?.summary]
  );

  const monthLabel = `${MONTH_OPTIONS[selectedMonth]} ${selectedYear}`;

  const yearOptions = Array.from({ length: currentYear - 2019 }, (_, index) => currentYear - index).map(
    (year) => ({ value: String(year), label: String(year) })
  );

  if (!accessReady) {
    return (
      <Page>
        <Loader label="Loading permissions…" />
      </Page>
    );
  }

  if (!isAllowed) {
    return (
      <Page>
        <PageHeader eyebrow="Teacher Operations" title="Teacher Ledger" />
        <Callout tone="danger" title="Access denied">
          You do not have permission to view Teacher Ledger.
        </Callout>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        eyebrow="Teacher Operations"
        title="Teacher Ledger"
        subtitle={
          selectedTeacher
            ? `${selectedTeacher.teacherName} · ${monthLabel}`
            : "Find a teacher to see their monthly payout and ledger lines."
        }
        actions={
          <Button
            variant={selectedTeacher ? "secondary" : "primary"}
            icon={<Search size={16} aria-hidden />}
            onClick={handleOpenTeacherSearch}
          >
            {selectedTeacher ? "Change teacher" : "Find teacher"}
          </Button>
        }
      />

      {!selectedTeacher ? (
        teacherProfileLoading ? (
          <Loader label="Loading teacher…" />
        ) : (
          <EmptyState
            icon={<Search size={22} aria-hidden />}
            title="No teacher selected"
            description="Use Find teacher to search by name, email or mobile number, then pick a month to see the ledger."
          />
        )
      ) : (
        <div className="tl-layout">
          <Card title={`${monthLabel} payout`}>
            <p className="tl-payout-total">
              {ledgerLoading ? "…" : formatSummaryTotalInr(ledgerData?.summary)}
            </p>
            <div className="tl-breakdown">
              {payoutBreakdown.map((item) => (
                <div key={item.key} className="tl-breakdown-row">
                  <span className="tl-breakdown-row__label">
                    <span className={`tl-dot ${item.dotClass}`} aria-hidden />
                    {item.countLabel != null ? `${item.label} (${item.countLabel})` : item.label}
                  </span>
                  <span className={`tl-breakdown-row__value ${item.valueText != null ? "" : item.amountClass}`}>
                    {item.valueText != null ? item.valueText : formatCurrency(item.amount)}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Ledger" className="tl-ledger">
            <Toolbar>
              <Field label="Month" className="tl-period">
                {(ctl) => (
                  <select
                    {...ctl}
                    className="ds-select"
                    value={String(selectedMonth)}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  >
                    {MONTH_OPTIONS.map((label, index) => (
                      <option key={label} value={index} disabled={isFutureMonth(index, selectedYear)}>
                        {label}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              <Field label="Year" className="tl-period">
                {(ctl) => (
                  <Select
                    {...ctl}
                    options={yearOptions}
                    value={String(selectedYear)}
                    onChange={(e) => {
                      const year = Number(e.target.value);
                      setSelectedYear(year);
                      if (isFutureMonth(selectedMonth, year)) {
                        setSelectedMonth(currentMonth);
                      }
                    }}
                  />
                )}
              </Field>
              <div className="ds-toolbar__end">
                <Button
                  variant="primary"
                  icon={<Plus size={16} aria-hidden />}
                  onClick={() => openAdjustmentModal({ mode: "create" })}
                  disabled={ledgerLoading}
                >
                  Add adjustment
                </Button>
              </div>
            </Toolbar>

            {ledgerTabs.length > 0 ? (
              <SegmentedControl
                label="Ledger filter"
                size="sm"
                value={activeFilter}
                onChange={setActiveFilter}
                options={ledgerTabs.map((tab) => ({
                  value: tab,
                  label: isAllLedgerTab(tab) ? "All" : formatLedgerTabLabel(tab),
                }))}
              />
            ) : null}

            {ledgerLoading ? (
              <Loader label="Loading ledger…" />
            ) : filteredGroups.length === 0 ? (
              <EmptyState
                title="No transactions"
                description={`Nothing in this filter for ${monthLabel}. Try All, or pick another month.`}
              />
            ) : (
              filteredGroups.map((group) => (
                <div key={group.dateKey} className="tl-day">
                  <div className="tl-day-divider">
                    <span>{group.date}</span>
                    <Badge tone={group.dayPositive ? "success" : "danger"}>
                      {group.dayPositive ? "+" : ""}₹{group.dayTotal.toLocaleString("en-IN")}
                    </Badge>
                  </div>

                  {group.transactions.map((transaction) => (
                    <div key={transaction.id} className={`tl-transaction ${transaction.borderColor}`}>
                      <div className="tl-transaction__body">
                        {(transaction.time || transaction.title) && (
                          <div className="tl-transaction__header">
                            {transaction.time ? <span className="tl-transaction__time">{transaction.time}</span> : null}
                            {transaction.title ? <span className="tl-transaction__title">{transaction.title}</span> : null}
                          </div>
                        )}
                        {transaction.detailLine ? <p className="tl-transaction__detail">{transaction.detailLine}</p> : null}
                        {(transaction.reason || transaction.description) && (
                          <div className="tl-transaction__meta">
                            {transaction.reason ? (
                              <p>
                                <span className="tl-transaction__meta-label">Reason:</span> {transaction.reason}
                              </p>
                            ) : null}
                            {transaction.description ? (
                              <p>
                                <span className="tl-transaction__meta-label">Description:</span>{" "}
                                {transaction.description}
                              </p>
                            ) : null}
                          </div>
                        )}
                        {transaction.badges.length > 0 && (
                          <div className="tl-badge-row">
                            {transaction.badges.map((badge) => (
                              <Badge key={badge.text} tone={BADGE_TONES[badge.className] ?? "neutral"}>
                                {badge.dot && <span className="tl-badge-dot" aria-hidden />}
                                {badge.text}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="tl-transaction__side">
                        <span className={`tl-transaction__amount ${transaction.amountColor}`}>
                          {formatCurrency(transaction.amount)}
                        </span>
                        {transaction.pendingReview === true ? (
                          <div className="tl-transaction__actions">
                            {transaction.canRejectDispute ? (
                              <Button
                                size="sm"
                                variant="danger"
                                loading={rejectSavingId === transaction.id}
                                onClick={() => openRejectModal(transaction)}
                              >
                                {rejectSavingId === transaction.id ? "Rejecting…" : "Reject"}
                              </Button>
                            ) : null}
                            <Button
                              size="sm"
                              variant="secondary"
                              icon={<Plus size={14} aria-hidden />}
                              onClick={() =>
                                openAdjustmentModal({
                                  mode: "update_line",
                                  ledgerLineId: Number(transaction.id),
                                  defaultAmount: transaction.amount,
                                  lineSummary: [
                                    transaction.time,
                                    transaction.title,
                                    transaction.detailLine || transaction.subtitle,
                                    formatCurrency(transaction.amount),
                                  ]
                                    .filter(Boolean)
                                    .join(" · "),
                                })
                              }
                            >
                              Add adjustment
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              ))
            )}
          </Card>
        </div>
      )}

      {selectedTeacher?.teacherId ? (
        <TeacherLedgerAdjustmentModal
          isOpen={adjustmentModalOpen}
          teacherId={selectedTeacher.teacherId}
          teacherName={selectedTeacher.teacherName}
          target={adjustmentTarget}
          onClose={closeAdjustmentModal}
          onSuccess={() => void fetchTeacherLedger()}
        />
      ) : null}

      <Dialog
        open={rejectModalOpen}
        onClose={closeRejectModal}
        title="Reject dispute?"
        actions={
          <>
            <Button variant="secondary" onClick={closeRejectModal} disabled={rejectSubmitting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => void handleRejectDispute()} loading={rejectSubmitting}>
              {rejectSubmitting ? "Rejecting…" : "Reject dispute"}
            </Button>
          </>
        }
      >
        {rejectTarget ? (
          <>
            <p className="tl-reject__line ds-text-muted">
              {[
                rejectTarget.time,
                rejectTarget.title,
                rejectTarget.detailLine || rejectTarget.subtitle,
                formatCurrency(rejectTarget.amount),
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
            <Field label="Reason" required>
              {(ctl) => (
                <TextArea
                  {...ctl}
                  rows={4}
                  value={rejectReason}
                  placeholder="e.g. Calculation is incorrect"
                  onChange={(e) => setRejectReason(e.target.value)}
                />
              )}
            </Field>
          </>
        ) : null}
      </Dialog>

      <Dialog open={teacherModalOpen} onClose={() => setTeacherModalOpen(false)} title="Select teacher" size="lg">
        <Toolbar>
          <SearchInput
            className="ds-toolbar__grow"
            aria-label="Search teachers"
            placeholder="Search by name, email, or mobile"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void fetchTeachers(searchQuery);
            }}
          />
          <div className="ds-toolbar__end">
            <Button variant="secondary" onClick={() => void fetchTeachers(searchQuery)}>
              Search
            </Button>
          </div>
        </Toolbar>

        {teacherSearchLoading ? (
          <Loader label="Searching teachers…" />
        ) : teacherResults.length === 0 ? (
          <EmptyState
            title="No teachers found"
            description="Check the spelling, or search by the full email or mobile number."
          />
        ) : (
          <Table caption="Teachers" stickyHeader wrapClassName="tl-teacher-scroll">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>
                  <span className="visually-hidden">Action</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {teacherResults.map((teacher) => (
                <tr
                  key={teacher.teacherId}
                  className="tl-teacher-row"
                  onClick={() => void handleSelectTeacher(teacher)}
                >
                  <td className="ds-strong">{teacher.teacherName}</td>
                  <td>{teacher.emailId || "—"}</td>
                  <td>{teacher.mobileNumber || "—"}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Select ${teacher.teacherName}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleSelectTeacher(teacher);
                      }}
                    >
                      Select
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Dialog>
    </Page>
  );
};

export default TeacherLedger;
